import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { previewCancel, cancelRide, cancelResultLine, canTryCancel } from '../lib/account';

// "Cancel ride" for an upcoming ride. The server's preview says what it costs right now; we show its words and the exact fee, then cancel through the same route.
export default function CancelRideBox({ ride, onCancelled }) {
  const ref = ride.reference || ride.id;
  const [step, setStep] = useState('idle');          // idle | loading | confirm | cancelling | done
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  if (!canTryCancel(ride) && step !== 'done') return null;

  const open = async () => {
    setError(''); setStep('loading');
    try {
      const p = await previewCancel(ref);
      if (!p.cancellable) { setError(p.message); setStep('idle'); return; }
      setPreview(p); setStep('confirm');
    } catch (e) { setError(e.message); setStep('idle'); }
  };
  const confirm = async () => {
    setError(''); setStep('cancelling');
    try {
      const res = await cancelRide(ref);
      setDone(cancelResultLine(preview, res)); setStep('done');
      if (onCancelled) onCancelled(ride, res);
    } catch (e) { setError(e.message); setStep('confirm'); }
  };

  if (step === 'done') return <div data-testid="cancel-done" className="mt-3 pt-3 border-t border-white/5 text-sm text-emerald-400">{done}</div>;
  const fee = Number(preview?.fee || 0);
  return (
    <div data-testid="cancel-box" className="mt-3 pt-3 border-t border-white/5">
      {step === 'confirm' || step === 'cancelling' ? (
        <div className={`rounded-xl border p-4 ${fee > 0 ? 'border-amber-400/40 bg-amber-400/[0.06]' : 'border-white/10 bg-white/[0.03]'}`}>
          <p data-testid="cancel-message" className="text-sm text-white">{preview.message}</p>
          {fee <= 0 && Number(preview.paid) > 0 && <p className="text-xs text-gray-400 mt-1">${Number(preview.refund).toFixed(2)} will be refunded to your card.</p>}
          {fee > 0 && Number(preview.refund) > 0 && <p className="text-xs text-gray-400 mt-1">${Number(preview.refund).toFixed(2)} will be refunded to your card.</p>}
          <div className="flex gap-2 mt-3">
            <button type="button" data-testid="cancel-keep" onClick={() => setStep('idle')} disabled={step === 'cancelling'}
              className="flex-1 rounded-lg border border-white/10 py-2 text-xs font-medium text-gray-300 hover:text-white disabled:opacity-40">Keep ride</button>
            <button type="button" data-testid="cancel-confirm" onClick={confirm} disabled={step === 'cancelling'}
              className="flex-1 rounded-lg bg-red-500/90 py-2 text-xs font-semibold text-white hover:bg-red-500 disabled:opacity-40">
              {step === 'cancelling' ? <Loader2 className="h-3.5 w-3.5 animate-spin mx-auto" /> : (fee > 0 ? `Yes, cancel and pay $${fee.toFixed(2)}` : 'Yes, cancel ride')}
            </button>
          </div>
        </div>
      ) : (
        <button type="button" data-testid="cancel-ride" onClick={open} disabled={step === 'loading'}
          className="w-full rounded-lg border border-red-400/30 py-2 text-xs font-medium text-red-300 hover:bg-red-400/10 disabled:opacity-40">
          {step === 'loading' ? <Loader2 className="h-3.5 w-3.5 animate-spin mx-auto" /> : 'Cancel ride'}
        </button>
      )}
      {error && <p data-testid="cancel-error" className="text-xs text-red-400 mt-2">{error}</p>}
    </div>
  );
}
